import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, RotateCcwIcon } from 'lucide-react';
import { useExperience } from '../../contexts/ExperienceContext';
import { flows } from '../../data/journeys';
import { SessionMemory } from './SessionMemory';
const stageNames: Record<string, string> = {
  'better-priority': 'Priority',
  'better-mirror': 'Reflection',
  'new-priority': 'What matters',
  'new-bridge': 'The idea',
  'existing-usage': 'Current use',
  'existing-competitor': 'Gaps',
  'existing-closer': 'Discovery',
  reveal: 'The idea',
  needs: 'The family',
  week: 'Your week',
  recommend: 'Your focus',
  family: 'Overview',
  deeper: 'Go deeper'
};
interface ExperienceChromeProps {
  showMemory: boolean;
}

/** Persistent, quiet frame: identity, where you are, and the session memory. */
export function ExperienceChrome({
  showMemory
}: ExperienceChromeProps) {
  const {
    route,
    stage,
    stageIndex,
    memory,
    accent,
    back,
    goTo,
    reset
  } = useExperience();
  const [open, setOpen] = useState(false);
  const onEntry = stage === 'entry';
  const sequence = route ? flows[route] : [];
  return <>
      <header className="pointer-events-none fixed inset-x-0 top-0 z-30 flex items-start justify-between px-6 py-5 sm:px-10 lg:px-16" data-id="element-7" data-mp="3h1xz7vunjp8t">
        <button type="button" onClick={reset} className="pointer-events-auto flex items-center gap-3 text-left" aria-label="Restart the Filtek Family experience" data-id="element-8" data-mp="37aa49kyvzq2l">
          <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true" data-id="element-9" data-mp="2xbkt0b1hx3vb">
            <rect x="3" y="4" width="18" height="4" rx="1" fill={accent} fillOpacity="0.85" data-id="element-10" data-mp="2ta8x5ns13meo" />
            <rect x="3" y="10" width="18" height="4" rx="1" fill="var(--enamel)" fillOpacity="0.45" data-id="element-11" data-mp="2ob5vmdb9bybg" />
            <rect x="3" y="16" width="18" height="4" rx="1" fill="var(--enamel)" fillOpacity="0.18" data-id="element-12" data-mp="mk2k7ltjmfpu" />
          </svg>
          <span className="leading-none" data-id="element-13" data-mp="1ag64j2b4cbni">
            <span className="label-mono block text-[10px] text-mute" data-id="element-14" data-mp="1ah8sp2h23zzq">Solventum</span>
            <span className="mt-1 block text-base font-light tracking-[-0.02em] text-enamel" data-id="element-15" data-mp="wpkjb6y6zpmg">
              Filtek Family
            </span>
          </span>
        </button>

        {showMemory && <button type="button" onClick={() => setOpen(true)} className="pointer-events-auto flex items-center gap-3 rounded-full border border-enamel/15 bg-ink/40 px-4 py-2 backdrop-blur-md transition-colors hover:border-enamel/40" data-id="element-16" data-mp="1pd6ky9nv0k6o">
            <span className="relative flex h-1.5 w-1.5" data-id="element-17" data-mp="1kbr1un58snqx">
              <span className="absolute inset-0 rounded-full" style={{
            backgroundColor: memory.length ? accent : 'var(--mute)'
          }} data-id="element-18" data-mp="3zc2bife7b9g" />
              {memory.length > 0 && <motion.span className="absolute inset-0 rounded-full" style={{
            backgroundColor: accent
          }} animate={{
            scale: [1, 3],
            opacity: [0.6, 0]
          }} transition={{
            duration: 2.2,
            repeat: Infinity,
            ease: 'easeOut'
          }} data-id="element-19" data-mp="1dupwxme1ovg" />}
            </span>
            <span className="label-mono text-[10px] text-enamel" data-id="element-20" data-mp="5e4x2dlx2lbw">
              Session {memory.length > 0 && `· ${memory.length}`}
            </span>
          </button>}
      </header>

      <AnimatePresence data-id="element-21" data-mp="39wafxhn67pxd">
        {!onEntry && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.5
      }} className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-4 px-6 py-5 sm:px-10 lg:px-16" data-id="element-22" data-mp="3nmdynymud1i2">
            <button type="button" onClick={back} className="pointer-events-auto group flex items-center gap-2.5 text-mute transition-colors hover:text-enamel" data-id="element-23" data-mp="2q66t31kk0dgl">
              <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" aria-hidden="true" data-id="element-24" data-mp="38pxkqq9kghqn" />
              <span className="label-mono text-[10px]" data-id="element-25" data-mp="3ijeg3ypcq4wf">Back</span>
            </button>

            <nav className="pointer-events-auto hidden items-center gap-1.5 sm:flex" aria-label="Journey progress" data-id="element-26" data-mp="3mj5bygtqjb7t">
              {sequence.map((step, index) => {
            const visited = index <= stageIndex;
            return <button key={step} type="button" disabled={!visited} onClick={() => goTo(step)} aria-label={stageNames[step] ?? step} aria-current={index === stageIndex ? 'step' : undefined} className="group relative py-3" data-id="element-27" data-mp="5a8ng97j2f3i" data-mpi={index}>
                    <span className="block h-px w-8 transition-all duration-500 lg:w-12" style={{
                backgroundColor: index === stageIndex ? accent : visited ? 'rgba(244,242,237,0.35)' : 'rgba(244,242,237,0.12)'
              }} data-id="element-28" data-mp="jgtvdwvt4kru" />
                    <span className="label-mono pointer-events-none absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[8px] text-mute opacity-0 transition-opacity duration-300 group-hover:opacity-100" data-id="element-29" data-mp="y5zor47yrgrt">
                      {stageNames[step] ?? step}
                    </span>
                  </button>;
          })}
            </nav>

            <button type="button" onClick={reset} className="pointer-events-auto group flex items-center gap-2.5 text-mute transition-colors hover:text-enamel" data-id="element-30" data-mp="3dvsz7gw2594f">
              <span className="label-mono text-[10px]" data-id="element-31" data-mp="3kb0542zhc994">Start over</span>
              <RotateCcwIcon className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-rotate-90" aria-hidden="true" data-id="element-32" data-mp="36d35sytn5oqr" />
            </button>
          </motion.div>}
      </AnimatePresence>

      <SessionMemory open={open} onClose={() => setOpen(false)} data-id="element-33" data-mp="31f1pglnlo3iz" />
    </>;
}